import apiInstance from "./api";

export type BlogStatus = "draft" | "published";

export interface BlogSeo {
	metaTitle: string;
	metaDescription: string;
	canonicalUrl: string;
	ogImageUrl: string;
	robotsIndex: boolean;
	robotsFollow: boolean;
}

export interface BlogCoverImage {
	url: string;
	key: string;
	focalX: number;
	focalY: number;
}

export interface BlogRecord {
	_id: string;
	title: string;
	slug: string;
	excerpt: string;
	contentHtml: string;
	contentJson: unknown;
	status: BlogStatus;
	publishedAt: string | null;
	coverImage: BlogCoverImage;
	seo: BlogSeo;
	archivedAt: string | null;
	updatedAt: string;
	createdAt: string;
}

export interface BlogListResponse {
	items: BlogRecord[];
	total: number;
	page: number;
	limit: number;
}

export interface BlogCommentAuthor {
	_id: string;
	username: string;
	role?: string;
}

export interface BlogCommentRecord {
	_id: string;
	blog: string;
	author: BlogCommentAuthor;
	content: string;
	createdAt: string;
	updatedAt: string;
}

export interface BlogCommentListResponse {
	items: BlogCommentRecord[];
	total: number;
	page: number;
	limit: number;
}

export const getAdminBlogs = async (params?: {
	q?: string;
	status?: BlogStatus;
	archived?: "active" | "all" | "archived";
	page?: number;
	limit?: number;
}) => apiInstance.get<BlogListResponse>("/blogs/admin", { params });

export const getAdminBlog = async (blogId: string) =>
	apiInstance.get<BlogRecord>(`/blogs/admin/${blogId}`);

export const createAdminBlog = async (payload: Partial<BlogRecord>) =>
	apiInstance.post<BlogRecord>("/blogs/admin", payload);

export const updateAdminBlog = async (
	blogId: string,
	payload: Partial<BlogRecord>,
) => apiInstance.put<BlogRecord>(`/blogs/admin/${blogId}`, payload);

export const updateBlogStatus = async (blogId: string, status: BlogStatus) =>
	apiInstance.patch<BlogRecord>(`/blogs/admin/${blogId}/status`, { status });

export const archiveBlog = async (blogId: string) =>
	apiInstance.post<BlogRecord>(`/blogs/admin/${blogId}/archive`);

export const restoreBlog = async (blogId: string) =>
	apiInstance.post<BlogRecord>(`/blogs/admin/${blogId}/restore`);

export const uploadBlogImage = async (file: File) => {
	const formData = new FormData();
	formData.append("image", file);
	return apiInstance.post<{ url: string; key: string }>(
		"/blogs/admin/upload-image",
		formData,
	);
};

export const getPublishedBlogs = async (params?: {
	page?: number;
	limit?: number;
}) => apiInstance.get<BlogListResponse>("/blogs", { params });

export const getPublishedBlogBySlug = async (slug: string) =>
	apiInstance.get<BlogRecord>(`/blogs/${slug}`);

export const getBlogComments = async (
	slug: string,
	params?: { page?: number; limit?: number },
) => apiInstance.get<BlogCommentListResponse>(`/blogs/${slug}/comments`, { params });

export const createBlogComment = async (slug: string, content: string) =>
	apiInstance.post<BlogCommentRecord>(`/blogs/${slug}/comments`, { content });

export const deleteBlogComment = async (commentId: string) =>
	apiInstance.delete<{ success: boolean }>(`/blogs/comments/${commentId}`);
